import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatInterval } from '../../utils/formatters';

/**
 * PlanCard Component
 *
 * Displays a single subscription plan with pricing and features.
 * Supports trial selection, plan change, and free plan activation.
 */
const PlanCard = ({
    plan,
    isCurrentPlan,
    isProcessing,
    hasActiveSubscription,
    canStartTrial,
    onSelect,
    onStartTrial,
    currentDogCount,
    isDowngradeBlocked,
    trialDays
}) => {
    const { t } = useTranslation('subscription');
    const isFree = plan.isFree || plan.price === 0;

    const handleSelectClick = () => {
        if (!isProcessing && !isCurrentPlan && !isDowngradeBlocked) {
            onSelect(plan);
        }
    };

    const handleTrialClick = () => {
        if (!isProcessing && onStartTrial) {
            onStartTrial(plan);
        }
    };

    // Determine primary button text
    const getButtonText = () => {
        if (isProcessing) return t('processing');
        if (isCurrentPlan) return t('currentPlan');
        if (isDowngradeBlocked) return t('tooManyClients');
        if (hasActiveSubscription) return t('changePlan');
        if (isFree) return t('chooseFree');
        return t('buyNow');
    };

    // Determine if primary button is disabled
    const isDisabled = isProcessing || isCurrentPlan || isDowngradeBlocked;

    // Show trial button only for paid plans when trial is available
    // canStartTrial from backend already accounts for free plan users (they can trial)
    const showTrialButton = canStartTrial && !isFree && !isCurrentPlan;

    return (
        <div
            className={`plan-card ${plan.recommended ? 'recommended' : ''} ${isCurrentPlan ? 'current' : ''} ${isDowngradeBlocked ? 'downgrade-blocked' : ''} ${isFree ? 'free-plan' : ''}`}
        >
            {plan.recommended && (
                <div className="badge recommended-badge">{t('recommended')}</div>
            )}
            {isCurrentPlan && (
                <div className="badge current-badge">{t('currentLabel')}</div>
            )}
            {isFree && !isCurrentPlan && (
                <div className="badge free-badge">{t('freePlan')}</div>
            )}

            <h2>{plan.name}</h2>

            <div className="price">
                {isFree ? (
                    <span className="amount free">0 zł</span>
                ) : (
                    <>
                        <span className="amount">
                            {formatCurrency(plan.price, plan.currency)}
                        </span>
                        <span className="period">
                            / {formatInterval(plan.period)}
                        </span>
                    </>
                )}
            </div>

            {plan.description && (
                <p className="plan-description">{plan.description}</p>
            )}

            <ul className="features">
                {plan.features.map((feature, index) => (
                    <li key={index}>
                        <span className="check">✔</span>
                        {feature}
                    </li>
                ))}
            </ul>

            {isDowngradeBlocked && (
                <div className="downgrade-warning">
                    {t('planCard.downgradeWarning', { current: currentDogCount, max: plan.maxDogs })}
                </div>
            )}

            <div className="plan-actions">
                {/* Trial button - shown for paid plans when trial available */}
                {showTrialButton && (
                    <button
                        className="trial-btn"
                        onClick={handleTrialClick}
                        disabled={isProcessing}
                    >
                        {isProcessing ? t('processing') : t('planCard.tryFree', { days: trialDays || 30 })}
                    </button>
                )}

                {/* Primary action button */}
                <button
                    className={`select-btn ${showTrialButton ? 'secondary' : 'primary'}`}
                    onClick={handleSelectClick}
                    disabled={isDisabled}
                >
                    {getButtonText()}
                </button>
            </div>
        </div>
    );
};

PlanCard.propTypes = {
    plan: PropTypes.shape({
        id: PropTypes.string.isRequired,
        code: PropTypes.string,
        priceId: PropTypes.string,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        price: PropTypes.number.isRequired,
        currency: PropTypes.string,
        period: PropTypes.string,
        features: PropTypes.arrayOf(PropTypes.string),
        recommended: PropTypes.bool,
        maxDogs: PropTypes.number,
        isFree: PropTypes.bool
    }).isRequired,
    isCurrentPlan: PropTypes.bool,
    isProcessing: PropTypes.bool,
    hasActiveSubscription: PropTypes.bool,
    canStartTrial: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onStartTrial: PropTypes.func,
    currentDogCount: PropTypes.number,
    isDowngradeBlocked: PropTypes.bool,
    trialDays: PropTypes.number
};

PlanCard.defaultProps = {
    isCurrentPlan: false,
    isProcessing: false,
    hasActiveSubscription: false,
    canStartTrial: false,
    onStartTrial: null,
    currentDogCount: 0,
    isDowngradeBlocked: false,
    trialDays: 30
};

export default PlanCard;
